import { useSpaceSummary } from 'atoms/spaces';
import FlexRow from 'components/flex/FlexRow';
import C from './SpaceParticipantOverview.css';
import React, { Fragment } from 'react';
import { Tooltip } from '@mui/material';
import NounGroupIcon from '!!react-svg-loader!../welcome-page/noun-group.svg';
import { useUserProfile } from '../../atoms/user-profile';
import { LightTooltip } from 'components/min-sidebar-community-list/MinSidebarCommunityList';
import { useLingui } from '@lingui/react';
import { Trans, msg } from '@lingui/macro';

function generateSpaceCountMessage(
  participantCount: number | null | undefined,
) {
  const { _ } = useLingui();

  if (!participantCount) {
    return _(msg`No one has joined this space yet`);
  } else if (participantCount == 1) {
    return _(msg`1 member has a project in this space`);
  } else {
    return _(msg`${participantCount} members have projects in this space`);
  }
}

function generateShortUsername(
  user: {
    firstName?: string | null;
    username?: string | null;
    userId?: string | null;
  },
  selfUserId: string,
) {
  const { _ } = useLingui();

  if (user.userId == selfUserId) {
    return _(msg`You`);
  } else if (user.firstName) {
    return user.firstName;
  } else {
    return user.username;
  }
}

const SpaceUsersFragment = React.forwardRef<
  HTMLDivElement,
  {
    users: {
      firstName?: string | null;
      username?: string | null;
      userId?: string | null;
    }[];
  }
>(function Component(p, ref) {
  const { userProfile } = useUserProfile();
  return (
    <div {...p} ref={ref} className={C.spaceUsers}>
      {p.users.map((user, idx, users) => (
        <Fragment key={user.userId}>
          <span>{generateShortUsername(user, userProfile?.id ?? '')}</span>
          {idx < users.length - 1 && <span> | </span>}
        </Fragment>
      ))}
    </div>
  );
});

export default function SpaceParticipantOverview(p: {
  spaceId: string;
  fontSize?: string;
  hideTooltip?: boolean;
}) {
  const { space } = useSpaceSummary({
    spaceId: p.spaceId,
  });

  const { userProfile } = useUserProfile();

  const participantCount = space?.participantCount;
  return (
    <FlexRow
      style={{
        marginBottom: 5,
        alignItems: 'center',
        fontSize: p.fontSize ? p.fontSize : 'var(--font-size-subtext)',
      }}
    >
      <LightTooltip title={generateSpaceCountMessage(participantCount)} arrow>
        <div className={C.spotCount}>
          <NounGroupIcon style={{ width: 15, height: 15 }} /> {participantCount}
        </div>
      </LightTooltip>
      {space?.participants && space.participants?.length > 0 ? (
        p.hideTooltip || space.participants?.length == 1 ? (
          <SpaceUsersFragment users={space.participants} />
        ) : (
          <LightTooltip
            title={space.participants
              .map((user) => generateShortUsername(user, userProfile?.id ?? ''))
              .join(', ')}
            arrow
            placement='top'
          >
            <SpaceUsersFragment users={space.participants} />
          </LightTooltip>
        )
      ) : (
        space.hosts && (
          <div className={C.otherHostTag}>
            <Trans>by</Trans>{' '}
            {space.hosts.map((user, idx, hosts) => (
              <Fragment key={user.id}>
                <span className={C.hostName}>
                  {generateShortUsername(user, userProfile?.id ?? '')}
                </span>
                {idx < hosts.length - 1 && <span>,</span>}
              </Fragment>
            ))}
          </div>
        )
      )}
    </FlexRow>
  );
}
